import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { CreateProductionOrderRequest } from "@opus-dt/shared-types";
import { Notify } from "@/components/ui/notify";
import { useBatchCharacteristics } from "@/features/organization/batch-characteristics/hooks";
import { CharacteristicValueField } from "@/features/batches/CharacteristicValueField";

/**
 * Spec 084 — as características da batelada, informadas NA ORDEM. Na tela, iniciar produção é
 * agendar: quando a hora chegar o worker inicia a batelada sozinho, e é daqui que ela herda a marca.
 * Só aparece quando a linha escolhida é linha de marcação de alguma característica (FR-001a).
 */
export function OrderCharacteristicsFields({
  form,
}: {
  form: UseFormReturn<CreateProductionOrderRequest>;
}) {
  const { watch, setValue } = form;
  const lineId = watch("lineId");
  const query = useBatchCharacteristics({ lineId, enabled: !!lineId });
  const values = watch("characteristics") ?? [];
  const items = query.data ?? [];

  // Trocar de linha descarta o que era da outra: o servidor recusaria valor de característica que
  // não é marcada na linha nova.
  const ids = items.map((i) => i.id).join(",");
  useEffect(() => {
    if (!query.data) return;
    const allowed = new Set(query.data.map((i) => i.id));
    const current = form.getValues("characteristics") ?? [];
    const kept = current.filter((v) => allowed.has(v.characteristicId));
    if (kept.length !== current.length) setValue("characteristics", kept, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reage à troca do conjunto de características
  }, [ids]);

  if (!lineId) return null;
  if (query.isError) {
    return (
      <Notify.QueryError
        error={query.error}
        onRetry={() => void query.refetch()}
        variant="inline"
      />
    );
  }
  if (items.length === 0) return null;

  const missing = items
    .filter((i) => i.required && !values.some((v) => v.characteristicId === i.id))
    .map((i) => i.name);

  return (
    <fieldset className="space-y-2 rounded-md border border-border-default p-3">
      <legend className="px-1 text-[13px] font-medium text-text-primary">
        Características da batelada
      </legend>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((i) => (
          <CharacteristicValueField
            key={i.id}
            idPrefix="po-characteristic"
            def={{ id: i.id, name: i.name, kind: i.kind, required: i.required, options: i.values }}
            value={values.find((v) => v.characteristicId === i.id)}
            onChange={(v) =>
              setValue(
                "characteristics",
                [...values.filter((x) => x.characteristicId !== i.id), ...(v ? [v] : [])],
                { shouldDirty: true }
              )
            }
          />
        ))}
      </div>
      {missing.length > 0 ? (
        <p role="status" className="text-[12px] text-rd-warning-700">
          Obrigatória nesta linha: {missing.join(", ")}.
        </p>
      ) : null}
    </fieldset>
  );
}
